<script setup lang="ts">
  import { getCurrentInstance, ref } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import { useFloorPlanCells } from '@/composition/use-floor-plan-cells';
  import type { ISector } from '@/interfaces/ISector';
  import type { IReservationGroupTableOption } from '@/domain/reservations/reservation-group-tables';

  const props = defineProps<{
    sector: ISector;
    /** Tables placed on this sector (position and size known). */
    tables: IReservationGroupTableOption[];
    selectedIds: number[];
    /** No further table can be added: unselected tiles stay visible but inert. */
    full?: boolean;
    disabled?: boolean;
    /** Members of a saved combination: double border and a link mark. */
    groupedIds?: number[];
    /** False makes the tile inert (aria-disabled, faded, clicks ignored); default always true. */
    selectable?: (tableId: number) => boolean;
    /** Extra classes for a tile, so a consumer can tint the button it cannot render itself. */
    tileClass?: (tableId: number) => string;
    /** Accessible name of a tile; default «{name} · {n} lugares[ · Desactivada]». */
    tileLabel?: (tableId: number) => string;
    /** Accessible name of the whole plan; default = the combination form's label. */
    label?: string;
  }>();
  const emit = defineEmits<{ (e: 'toggle', tableId: number): void }>();

  const proxy = getCurrentInstance()!.proxy;
  const containerRef = ref<HTMLElement | null>(null);
  // 52px keeps a one-cell table tappable AND leaves room for its name and
  // seats on two lines (wider grids scroll sideways); the low height cap
  // leaves room for the summary and fields under the plan.
  const { cellPx, gridStyle } = useFloorPlanCells(
    containerRef,
    () => props.sector,
    { minCell: 52, heightRatio: 0.45, minHeight: 280, inset: 4 }
  );

  function isSelected(table: IReservationGroupTableOption) {
    return props.selectedIds.includes(table.id);
  }

  function isGrouped(table: IReservationGroupTableOption) {
    return !!props.groupedIds && props.groupedIds.includes(table.id);
  }

  function isSelectable(table: IReservationGroupTableOption) {
    return props.selectable ? props.selectable(table.id) : true;
  }

  // Small tiles keep both lines in a smaller type and drop the check mark.
  function isCompact(table: IReservationGroupTableOption) {
    return table.w * cellPx.value < 64 || table.h * cellPx.value < 44;
  }

  function tileStyle(table: IReservationGroupTableOption) {
    const cell = cellPx.value;
    return {
      left: `${(table.posX ?? 0) * cell}px`,
      top: `${(table.posY ?? 0) * cell}px`,
      width: `${table.w * cell}px`,
      height: `${table.h * cell}px`,
    };
  }

  function tileLabel(table: IReservationGroupTableOption) {
    if (props.tileLabel) return props.tileLabel(table.id);
    const seats = proxy.$t('reservations.timeline.seats', {
      capacity: table.capacity,
    });
    const state = table.enabled
      ? ''
      : ` · ${proxy.$t('reservations.tables.disabled')}`;
    return `${table.name} · ${seats}${state}`;
  }

  function tileClasses(table: IReservationGroupTableOption) {
    return [
      props.tileClass ? props.tileClass(table.id) : '',
      {
        selected: isSelected(table),
        inactive: !table.enabled,
        grouped: isGrouped(table),
        inert: !isSelectable(table),
        'is-round': table.shape === 'round',
        'is-compact': isCompact(table),
      },
    ];
  }

  function toggle(table: IReservationGroupTableOption) {
    if (!isSelectable(table)) return;
    emit('toggle', table.id);
  }
</script>

<template>
  <div
    ref="containerRef"
    class="reservation-floor-plan-picker"
    role="group"
    :aria-label="label || $t('reservations.groups.form.tables')"
  >
    <!-- Arrows on touch screens: wide plans scroll sideways on phones. -->
    <PosUiScrollable orientation="horizontal" arrows hide-scrollbar="touch">
      <div class="plan-grid" :style="gridStyle">
        <button
          v-for="table in tables"
          :key="table.id"
          type="button"
          class="plan-table table-option"
          :class="tileClasses(table)"
          :style="tileStyle(table)"
          :disabled="disabled || (!isSelected(table) && full)"
          :aria-pressed="isSelected(table) ? 'true' : 'false'"
          :aria-disabled="isSelectable(table) ? undefined : 'true'"
          :aria-label="tileLabel(table)"
          @click="toggle(table)"
        >
          <slot name="tile" :table="table" :compact="isCompact(table)">
            <PosUiIcon
              v-if="isSelected(table)"
              class="plan-table-check"
              name="check-circle"
              :size="16"
            />
            <span class="plan-table-name">{{ table.name }}</span>
            <span class="plan-table-seats"
              >{{
                $t('reservations.tables.editor.tilePeople', {
                  count: table.capacity,
                })
              }}<template v-if="!table.enabled">
                · {{ $t('reservations.tables.disabled') }}</template
              ></span
            >
          </slot>
          <PosUiIcon
            v-if="isGrouped(table)"
            class="plan-table-link"
            name="link-variant"
            :size="14"
          />
        </button>
      </div>
    </PosUiScrollable>
  </div>
</template>

<style scoped lang="scss">
  .reservation-floor-plan-picker {
    @apply tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-p-2;
    .plan-grid {
      // 2px of room so the focus ring of edge tiles is not clipped.
      @apply tw-relative tw-m-0.5 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
      background-image: linear-gradient(
          to right,
          rgba(0, 0, 0, 0.045) 1px,
          transparent 1px
        ),
        linear-gradient(to bottom, rgba(0, 0, 0, 0.045) 1px, transparent 1px);
    }
    .plan-table {
      @apply tw-absolute tw-flex tw-cursor-pointer tw-select-none tw-flex-col tw-items-center tw-justify-center tw-gap-0.5 tw-overflow-hidden tw-rounded-lg tw-border-2 tw-border-solid tw-border-neutral-300 tw-bg-white tw-p-0.5 tw-text-neutral-800 tw-transition-colors hover:tw-border-neutral-500 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40;
      &.is-round {
        @apply tw-rounded-full;
      }
      // A combination member; the selected look below wins while pressed.
      &.grouped:not(.selected) {
        @apply tw-border-[3px] tw-border-double tw-border-neutral-400;
      }
      &.selected {
        @apply tw-border-orange-600 tw-bg-orange-50 tw-text-orange-900 hover:tw-border-orange-700;
        z-index: 1;
      }
      &.inactive {
        @apply tw-border-dashed tw-text-neutral-500;
      }
      // Not selectable right now: faded, and hover must not promise a click.
      &.inert {
        @apply tw-cursor-default tw-opacity-60 hover:tw-border-neutral-300;
      }
      &.is-round .plan-table-check {
        @apply tw-right-3 tw-top-3;
      }
      &.is-round .plan-table-link {
        @apply tw-left-3 tw-top-3;
      }
      &:disabled:not(.selected) {
        @apply tw-cursor-not-allowed tw-opacity-40;
      }
      .plan-table-check {
        @apply tw-absolute tw-right-1 tw-top-1 tw-text-orange-600;
      }
      .plan-table-link {
        @apply tw-absolute tw-left-1 tw-top-1 tw-text-neutral-500;
      }
      .plan-table-name {
        @apply tw-max-w-full tw-truncate tw-px-1 tw-text-xs tw-font-semibold;
      }
      .plan-table-seats {
        @apply tw-max-w-full tw-truncate tw-px-0.5 tw-text-[10px] tw-leading-none tw-text-neutral-500;
      }
      &.selected .plan-table-seats {
        @apply tw-text-orange-800;
      }
      // Both lines stay: an owner must recognise a table without tapping it.
      &.is-compact {
        .plan-table-check {
          @apply tw-hidden;
        }
        .plan-table-name {
          @apply tw-px-0.5 tw-text-[10px];
        }
        .plan-table-seats {
          @apply tw-text-[9px];
        }
      }
    }
  }
</style>
